import {
  collection,
  limit,
  onSnapshot,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { useEffect, useState } from "react";
import { firestore } from "../firebase";
import { CheckInEventData } from "../types/CheckInEvent";

export interface PlaceCheckInStatus {
  isLoading: boolean;
  isCheckedInElsewhere: boolean;
  isCheckedInAtThisPlace: boolean;
  currentCheckInEventId: string | null;
  checkedInPlaceName: string | null;
  checkedInPlaceId: string | null;
}

/**
 * Hook to determine the user's check-in status relative to a specific place using real-time updates.
 *
 * @param placeId - The ID of the place to check status against.
 * @param userId - The ID of the current user.
 * @returns An object with the user's check-in status that updates in real-time.
 */
export const usePlaceCheckInStatus = (
  placeId: string | undefined | null,
  userId: string | undefined
): PlaceCheckInStatus => {
  const [isLoading, setIsLoading] = useState(true);
  const [isCheckedInAtThisPlace, setIsCheckedInAtThisPlace] = useState(false);
  const [isCheckedInElsewhere, setIsCheckedInElsewhere] = useState(false);
  const [currentCheckInEventId, setCurrentCheckInEventId] = useState<
    string | null
  >(null);
  const [checkedInPlaceName, setCheckedInPlaceName] = useState<string | null>(
    null
  );
  const [checkedInPlaceId, setCheckedInPlaceId] = useState<string | null>(null);

  useEffect(() => {
    if (!userId) {
      setIsLoading(false);
      setIsCheckedInAtThisPlace(false);
      setIsCheckedInElsewhere(false);
      setCurrentCheckInEventId(null);
      setCheckedInPlaceName(null);
      setCheckedInPlaceId(null);
      return;
    }

    setIsLoading(true);

    const checkInQuery = query(
      collection(firestore, "checkInEvents"),
      where("userId", "==", userId),
      where("checkOutTime", "==", null),
      orderBy("checkInTime", "desc"),
      limit(1)
    );

    const unsubscribe = onSnapshot(
      checkInQuery,
      (snapshot) => {
        // Reset states
        setCurrentCheckInEventId(null);
        setCheckedInPlaceName(null);
        setCheckedInPlaceId(null);
        setIsCheckedInAtThisPlace(false);
        setIsCheckedInElsewhere(false);

        if (!snapshot.empty) {
          const doc = snapshot.docs[0];
          const activeCheckIn = {
            id: doc.id,
            ...doc.data(),
          } as CheckInEventData;

          setCurrentCheckInEventId(activeCheckIn.id);
          setCheckedInPlaceName(activeCheckIn.placeName);
          setCheckedInPlaceId(activeCheckIn.placeId);

          if (activeCheckIn.placeId === placeId) {
            setIsCheckedInAtThisPlace(true);
          } else {
            setIsCheckedInElsewhere(true);
          }
        }

        setIsLoading(false);
      },
      (error) => {
        console.error("Error listening to check-in status:", error);

        // Reset states on error
        setIsCheckedInAtThisPlace(false);
        setIsCheckedInElsewhere(false);
        setCurrentCheckInEventId(null);
        setCheckedInPlaceName(null);
        setCheckedInPlaceId(null);
        setIsLoading(false);
      }
    );

    return () => unsubscribe();
  }, [userId, placeId]);

  return {
    isLoading,
    isCheckedInAtThisPlace,
    isCheckedInElsewhere,
    currentCheckInEventId,
    checkedInPlaceName,
    checkedInPlaceId,
  };
};
